import { useState, useRef } from 'react';
import { useBulkImportCSV, useBulkImportRelCSV, useBulkImportJSON } from '../api/intelligence';

export default function ImportPage() {
  const [tab, setTab] = useState<'entities' | 'relationships' | 'json'>('entities');
  const fileRef = useRef<HTMLInputElement>(null);
  const csvMutation = useBulkImportCSV();
  const relMutation = useBulkImportRelCSV();
  const jsonMutation = useBulkImportJSON();

  const activeMutation = tab === 'entities' ? csvMutation : tab === 'relationships' ? relMutation : jsonMutation;

  const handleUpload = () => {
    const file = fileRef.current?.files?.[0];
    if (!file) return;
    activeMutation.mutate(file);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-gray-100">Import en masse</h1>
        <p className="text-sm text-gray-500 mt-1">Importer des entites et relations depuis CSV ou JSON</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-surface-tertiary/50 p-1 rounded-lg w-fit">
        {[
          { key: 'entities' as const, label: 'Entites CSV' },
          { key: 'relationships' as const, label: 'Relations CSV' },
          { key: 'json' as const, label: 'JSON complet' },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => { setTab(t.key); activeMutation.reset(); }}
            className={`px-4 py-2 text-sm rounded-md transition-colors ${tab === t.key ? 'bg-surface-secondary text-cyber-cyan' : 'text-gray-400 hover:text-gray-200'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Template info */}
      <div className="glass-panel p-5 space-y-3">
        <h3 className="text-sm font-semibold text-gray-300">Format attendu</h3>
        {tab === 'entities' && (
          <div className="text-xs text-gray-400 space-y-1">
            <p>CSV avec colonnes : <code className="px-1 py-0.5 bg-surface-tertiary rounded text-cyber-cyan">name, entity_type, properties</code></p>
            <p>entity_type : person, organization, location, vehicle, domain, ip_address, email, phone_number</p>
            <p>properties : chaine JSON (optionnel), ex: {`{"nationality":"FR","age":45}`}</p>
            <div className="mt-2 p-3 bg-surface-primary rounded-lg font-mono text-[11px]">
              name,entity_type,properties<br />
              Jean Dupont,person,{`{"nationality":"FR"}`}<br />
              Acme Corp,organization,{`{"sector":"finance"}`}<br />
              Paris,location,{`{"lat":48.85,"lng":2.35}`}
            </div>
          </div>
        )}
        {tab === 'relationships' && (
          <div className="text-xs text-gray-400 space-y-1">
            <p>CSV avec colonnes : <code className="px-1 py-0.5 bg-surface-tertiary rounded text-cyber-cyan">source_name, target_name, relation_type, weight</code></p>
            <p>Les entites sont retrouvees par leur nom exact.</p>
            <div className="mt-2 p-3 bg-surface-primary rounded-lg font-mono text-[11px]">
              source_name,target_name,relation_type,weight<br />
              Jean Dupont,Acme Corp,employed_by,0.9<br />
              Acme Corp,Paris,located_in,1.0
            </div>
          </div>
        )}
        {tab === 'json' && (
          <div className="text-xs text-gray-400 space-y-1">
            <p>Fichier JSON contenant un tableau d'entites avec relations optionnelles.</p>
            <div className="mt-2 p-3 bg-surface-primary rounded-lg font-mono text-[11px]">
              {`[{"name":"Jean","entity_type":"person","properties":{}}]`}
            </div>
          </div>
        )}
      </div>

      {/* Upload */}
      <div className="glass-panel p-5 space-y-4">
        <div className="flex items-center gap-4">
          <input
            ref={fileRef}
            type="file"
            accept={tab === 'json' ? '.json' : '.csv'}
            className="text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-cyber-cyan/10 file:text-cyber-cyan hover:file:bg-cyber-cyan/20 file:cursor-pointer file:transition-colors"
          />
          <button
            onClick={handleUpload}
            disabled={activeMutation.isPending}
            className="px-5 py-2 text-sm font-medium bg-cyber-cyan/10 text-cyber-cyan rounded-lg hover:bg-cyber-cyan/20 disabled:opacity-50 transition-colors"
          >
            {activeMutation.isPending ? 'Import en cours...' : 'Importer'}
          </button>
        </div>

        {/* Results */}
        {activeMutation.data && (
          <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg space-y-2">
            <p className="text-sm font-medium text-green-400">Import termine</p>
            <p className="text-xs text-gray-400">
              {activeMutation.data.imported} importes sur {activeMutation.data.total_rows} lignes
            </p>
            {activeMutation.data.errors && activeMutation.data.errors.length > 0 && (
              <div className="mt-2 space-y-1">
                <p className="text-xs font-medium text-red-400">{activeMutation.data.errors.length} erreur(s) :</p>
                {activeMutation.data.errors.slice(0, 10).map((err: { row: number; error: string }, i: number) => (
                  <p key={i} className="text-xs text-gray-500">
                    Ligne {err.row}: {err.error}
                  </p>
                ))}
              </div>
            )}
          </div>
        )}

        {activeMutation.isError && (
          <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
            <p className="text-sm text-red-400">Erreur lors de l'import</p>
          </div>
        )}
      </div>
    </div>
  );
}
